import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, IconButton, Typography, Box, Avatar, Menu, MenuItem,
  Chip, Select, FormControl, ListItemIcon, ListItemText
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { glass, glassFallback } from '../../theme';
import '../motion/motion.css';
import { BELL_SLOT_ID } from '../motion/NotificationBell';
import PersonIcon from '@mui/icons-material/Person';
import { logout } from '../../store/authSlice';
import { setSelectedProvider } from '../../store/cloudSlice';
import AccountSelector from './AccountSelector';
import OrgSwitcher from './OrgSwitcher';

const PROVIDERS = [
  { value: 'all',   label: 'All Platforms', icon: '☁️' },
  { value: 'aws',   label: 'AWS', icon: '🟠' },
  { value: 'azure', label: 'Azure', icon: '🔵' },
  { value: 'gcp',   label: 'GCP', icon: '🟢' }
];


export default function Navbar({ onMenuClick, onRefresh }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const selectedProvider = useSelector((state) => state.cloud.selectedProvider);
  const orgCount = useSelector((state) => state.organizations.list.length);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [launching, setLaunching] = React.useState(false);
  const launchTimer = React.useRef(null);
  React.useEffect(() => () => clearTimeout(launchTimer.current), []);

  const handleRefresh = () => {
    if (launching) return;
    setLaunching(true);
    onRefresh?.();
    launchTimer.current = setTimeout(() => setLaunching(false), 700);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    dispatch(logout());
    navigate('/login');
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: glass.header,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        ...glassFallback(),
        color: 'text.primary',
        borderBottom: glass.border,
        zIndex: (theme) => theme.zIndex.drawer - 1
      }}
    >
      <Toolbar sx={{ gap: 1, flexWrap: { xs: 'wrap', md: 'nowrap' }, py: { xs: 1, md: 0 } }}>
        <IconButton
          edge="start"
          onClick={onMenuClick}
          aria-label="Open navigation"
          sx={{ mr: 1, display: { md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        {/* Platform Selector */}
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <Select
            value={selectedProvider}
            onChange={(e) => dispatch(setSelectedProvider(e.target.value))}
            displayEmpty
            renderValue={(val) => {
              const p = PROVIDERS.find(x => x.value === val);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: 16 }} aria-hidden="true">{p?.icon}</span>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {p?.label}
                  </Typography>
                </Box>
              );
            }}
            inputProps={{ 'aria-label': 'Cloud platform' }}
            sx={{ '& .MuiSelect-select': { py: 0.8 } }}
          >
            {PROVIDERS.map(p => (
              <MenuItem key={p.value} value={p.value}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <span style={{ fontSize: 16 }} aria-hidden="true">{p.icon}</span>
                </ListItemIcon>
                <ListItemText
                  primary={p.label}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Account Selector (filtered by selectedProvider) */}
        <AccountSelector />

        <Box sx={{ flexGrow: 1 }} />

        <OrgSwitcher />

        {onRefresh && (
          <span className={`svx-launch ${launching ? 'is-launching' : ''}`}>
            <IconButton onClick={handleRefresh} aria-label="Refresh data" title="Refresh data">
              <span className="svx-launch__icon"><RocketLaunchIcon fontSize="small" /></span>
            </IconButton>
          </span>
        )}
        <Box id={BELL_SLOT_ID} sx={{ display: 'contents' }} />

        {/* Static company chip only for single-org users — OrgSwitcher already
            shows the active org's name once a user has 2+, and re-showing
            user.companyName here too would risk displaying a stale value
            after a switch (that field isn't re-derived per-org). */}
        {user?.companyName && orgCount < 2 && (
          <Chip
            label={user.companyName}
            size="small"
            sx={{ mr: 1 }}
          />
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
          {user?.email}
        </Typography>

        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Account menu"
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', color: 'primary.dark' }}>
            <PersonIcon fontSize="small" />
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem disabled>
            <Typography variant="body2">{user?.role}</Typography>
          </MenuItem>
          <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
